import { useState } from "react";
import { Link } from "react-router-dom";
import { Select, DatePicker } from "antd";
import dayjs from "dayjs";
import { Chart as ChartJS, registerables } from "chart.js";
import "chartjs-plugin-annotation";
import withDashboardLayout from "../../hoc/withDashboardLayout";
import { CaretDownOutlined } from "@ant-design/icons";
import GoogleAnalyticsImg from "../../assets/dashboard/google-analytics.svg";
import Widgets from "../../components/admin/dashboard/Widgets";
import TotalSignupsChart from "../../components/admin/dashboard/TotalSignupsChart";
import TotalRevenueChart from "../../components/admin/dashboard/TotalRevenueChart";
import CommisionRevenueChart from "../../components/admin/dashboard/CommisionRevenueChart";
import CustomerRetentionRateChart from "../../components/admin/dashboard/CustomerRetentionRateChart";
import AccountsDeletedChart from "../../components/admin/dashboard/AccountsDeletedChart";
import ServiceProviderCategoriesChart from "../../components/admin/dashboard/ServiceProviderCategoriesChart";
import MostViewedServiceProviders from "../../components/admin/dashboard/MostViewedServiceProviders";
import { AnalyticsLink } from "../../utils/axiosInstance";
ChartJS.register(...registerables);

const DashboardFilterDateFormat = "YYYY-MM-DD";

const Dashboard = () => {
  const { RangePicker } = DatePicker;

  const [selectedOption, setSelectedOption] = useState("thisWeek");
  const [startDate, setStartDate] = useState(
    dayjs().startOf("week").format(DashboardFilterDateFormat)
  );
  const [endDate, setEndDate] = useState(
    dayjs().endOf("week").format(DashboardFilterDateFormat)
  );

  const handleSelectChange = (option) => {
    setSelectedOption(option);
    switch (option) {
      case "today":
        setStartDate(dayjs().startOf("day").format("YYYY-MM-DD HH:mm:ss"));
        setEndDate(dayjs().endOf("day").format("YYYY-MM-DD HH:mm:ss"));
        break;
      case "thisWeek":
        setStartDate(dayjs().startOf("week").format(DashboardFilterDateFormat));
        setEndDate(dayjs().endOf("week").format(DashboardFilterDateFormat));
        break;
      case "lastMonth":
        setStartDate(
          dayjs()
            .subtract(1, "month")
            .startOf("month")
            .format(DashboardFilterDateFormat)
        );
        setEndDate(
          dayjs()
            .subtract(1, "month")
            .endOf("month")
            .format(DashboardFilterDateFormat)
        );
        break;
      case "quarter":
        const currentMonth = dayjs().month();
        const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
        const quarterStart = dayjs()
          .month(quarterStartMonth)
          .startOf("month")
          .format(DashboardFilterDateFormat);
        const quarterEndMonth = quarterStartMonth + 2;
        const quarterEnd = dayjs()
          .month(quarterEndMonth)
          .endOf("month")
          .format(DashboardFilterDateFormat);
        setStartDate(quarterStart);
        setEndDate(quarterEnd);
        break;
      case "thisYear":
        setStartDate(dayjs().startOf("year").format(DashboardFilterDateFormat));
        setEndDate(dayjs().endOf("year").format(DashboardFilterDateFormat));
        break;
      default:
        setStartDate(null);
        setEndDate(null);
    }
  };

  const onRangeChange = (dates) => {
    if (dates === null) {
      setEndDate(null);
      setStartDate(null);
    } else if (dates.length === 2) {
      const [start, end] = dates.map((date) =>
        date.format(DashboardFilterDateFormat)
      );
      setStartDate(start);
      setEndDate(end);
    }
  };

  return (
    <div className="w-full h-full">
      <div className="w-[95%] mx-auto">
        {/* Dashboard Header */}
        <div className="w-full py-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-lg title text-[#000000]">
              Dashboard
            </h2>
            <div className="flex items-center gap-x-3">
              <div className="w-full flex items-center justify-end gap-x-2">
                {selectedOption === "" ? (
                  <div>
                    <RangePicker
                      value={[
                        startDate ? dayjs(startDate) : null,
                        endDate ? dayjs(endDate) : null,
                      ]}
                      autoFocus
                      onChange={onRangeChange}
                      format="D MMM YY"
                      disabledDate={(current) =>
                        current && current > dayjs().endOf("day")
                      }
                    />
                  </div>
                ) : (
                  ""
                )}
                <Select
                  suffixIcon={<CaretDownOutlined />}
                  className="w-32"
                  value={selectedOption}
                  onChange={(option) => handleSelectChange(option)}
                >
                  <Select.Option value="today">Today</Select.Option>
                  <Select.Option value="thisWeek">This Week</Select.Option>
                  <Select.Option value="lastMonth">Last Month</Select.Option>
                  <Select.Option value="quarter">This Quarter</Select.Option>
                  <Select.Option value="thisYear">This Year</Select.Option>
                  <Select.Option value="">Custom</Select.Option>
                </Select>
              </div>

              <Link
                to={AnalyticsLink}
                target="_blank"
                className="sm:flex hidden flex-shrink-0 items-center gap-x-2 text-[10px] bg-white rounded-md p-2 cursor-pointer"
              >
                <img
                  className="w-3 h-3"
                  src={GoogleAnalyticsImg}
                  alt="google-analytics"
                />
                <span className="text-[#818181] text-xs">Google Analytics</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Dashboard */}
        {/* <div className="w-full min-h-[calc(100vh-110px)] flex-col gap-y-5 box-shadow rounded-t-2xl p-5 bg-white hidden"> */}
        <div className="w-full min-h-[calc(100vh-110px)] flex flex-col gap-y-5 box-shadow rounded-t-2xl p-5 bg-white">
          {/* Numeric Stats */}
          <Widgets
            selectedOption={selectedOption}
            startDate={startDate}
            endDate={endDate}
          />

          {/* Charts */}
          <div className="w-full flex flex-col sm:flex-row gap-5">
            {/* Total Signups chart */}
            <TotalSignupsChart
              selectedOption={selectedOption}
              startDate={startDate}
              endDate={endDate}
            />

            {/* Total Revenue chart */}
            <TotalRevenueChart
              selectedOption={selectedOption}
              startDate={startDate}
              endDate={endDate}
            />
          </div>

          <div className="w-full flex flex-col sm:flex-row gap-5">
            {/* Commission Revenue chart */}
            <CommisionRevenueChart
              selectedOption={selectedOption}
              startDate={startDate}
              endDate={endDate}
            />

            {/* Customer Retention Rate chart */}
            <CustomerRetentionRateChart
              selectedOption={selectedOption}
              startDate={startDate}
              endDate={endDate}
            />
          </div>

          {/* Other Stats */}
          <div className="w-full flex flex-col sm:flex-row gap-5">
            {/* Most viewed Service Providers */}
            <MostViewedServiceProviders />

            {/* Service Provider Categories chart */}
            <ServiceProviderCategoriesChart />

            {/* Accounts Deleted chart*/}
            <AccountsDeletedChart
              selectedOption={selectedOption}
              startDate={startDate}
              endDate={endDate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default withDashboardLayout(Dashboard);
